/**
 * Token Service - Centralized token management
 * 
 * This service manages authentication tokens with support for:
 * 1. httpOnly cookies (set by server, more secure)
 * 2. localStorage fallback (for backward compatibility)
 * 
 * The server sets httpOnly cookies on login/register.
 * We still store the token in localStorage for:
 * - Initial page load detection (before cookie is validated)
 * - Backward compatibility with API clients
 */

import axios from 'axios';

const TOKEN_KEY = 'token';
const PORTAL_KEY = 'active_portal';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Token storage abstraction
const storage = {
  get: (key) => localStorage.getItem(key),
  set: (key, value) => localStorage.setItem(key, value),
  remove: (key) => localStorage.removeItem(key),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PORTAL_KEY);
  }
};

export const tokenService = {
  // Get the current auth token
  getToken: () => storage.get(TOKEN_KEY),
  
  // Set the auth token (called after login response)
  setToken: (token) => {
    if (token) {
      storage.set(TOKEN_KEY, token);
    } else {
      storage.remove(TOKEN_KEY);
    }
  },
  
  // Remove the auth token (logout)
  removeToken: () => storage.remove(TOKEN_KEY),
  
  // Check if user is authenticated
  isAuthenticated: () => !!storage.get(TOKEN_KEY),
  
  // Portal management for dual-role users
  getActivePortal: () => storage.get(PORTAL_KEY),
  setActivePortal: (portal) => {
    if (portal) {
      storage.set(PORTAL_KEY, portal);
    } else {
      storage.remove(PORTAL_KEY);
    }
  },
  
  // Clear all auth data (full logout) - also calls server to clear cookie
  clearAll: async () => {
    storage.clear();
    // Call server to clear httpOnly cookie
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore errors - cookie will expire eventually
      console.debug('Logout endpoint error (non-critical):', e.message);
    }
  },
  
  // Sync version for immediate cleanup (without waiting for server)
  clearAllSync: () => {
    storage.clear();
  }
};

export default tokenService;
